import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

import noHarvestsPng from '@/assets/illustrations/empty/no-harvests.png';

import { useAuthStore } from '@/stores/auth-store';
import { db } from '@/services/storage/db';
import type { HarvestRecord } from '@/types/hunt';
import { HarvestTrophyCard } from '@/components/hunts/HarvestTrophyCard';
import { OperationalGuide } from '@/components/common/OperationalGuide';
import { useHarvestStore } from '@/stores/harvest-store';

/** HarvestGalleryPage — shows all harvest records for the current user, sorted newest-first. */
export function HarvestGalleryPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { deleteRecord } = useHarvestStore();

  const [records, setRecords] = useState<HarvestRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const userId = user?.id;

  useEffect(() => {
    if (!userId) return;
    db.getAllHarvestRecordsByUser(userId)
      .then((all) => {
        const sorted = [...all].sort((a, b) => b.date.localeCompare(a.date));
        setRecords(sorted);
      })
      .catch((err: unknown) => console.error('[HarvestGalleryPage] load failed:', err))
      .finally(() => setIsLoading(false));
  }, [userId]);

  async function handleDelete(harvestId: string) {
    await deleteRecord(harvestId);
    setRecords((prev) => prev.filter((r) => r.id !== harvestId));
  }

  // ROOT-CAUSE (I-7): OperationalGuide standard variant uses min-h-[calc(100vh-8rem)]
  // for centering, but that calculation is from the viewport top — not from after the
  // header stack. When back button + H1 consume ~5rem, the center point of the
  // remaining space is pushed down. Wrapping the empty state in a flex-1 container
  // gives it all remaining vertical space and centers correctly.

  if (!isLoading && records.length === 0) {
    return (
      <div
        className="flex flex-col min-h-[calc(100vh-8rem)]"
        data-testid="harvest-gallery-page"
      >
        {/* Back nav */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-body-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors -ml-1 self-start mb-4"
          data-testid="back-button"
        >
          <ChevronLeft size={16} aria-hidden="true" />
          More
        </button>

        <h1
          className="font-display text-display-lg text-[var(--color-text-display)] mb-4"
          data-testid="harvest-gallery-title"
        >
          Harvest Records
        </h1>

        {/* Centered empty state — fills remaining vertical space */}
        <div
          className="flex-1 flex items-center justify-center"
          data-layout="centered-empty"
          data-testid="harvest-empty-centered"
        >
          <OperationalGuide
            illustration={noHarvestsPng}
            heading="No Trophies Yet"
            subtext="Your successful harvests will appear here as a seasonal record."
            data-guide-id="harvest-gallery-empty"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="harvest-gallery-page">
      {/* Back nav */}
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-body-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors -ml-1 self-start"
        data-testid="back-button"
      >
        <ChevronLeft size={16} aria-hidden="true" />
        More
      </button>

      <h1
        className="font-display text-display-lg text-[var(--color-text-display)]"
        data-testid="harvest-gallery-title"
      >
        Harvest Records
      </h1>

      {isLoading && (
        <p className="text-body-sm text-[var(--color-text-muted)]" data-testid="harvest-gallery-loading">
          Loading…
        </p>
      )}

      {!isLoading && records.length > 0 && (
        <div className="flex flex-col gap-3" data-testid="harvest-gallery-list">
          {records.map((record) => (
            <HarvestTrophyCard
              key={record.id}
              record={record}
              huntId={record.huntId}
              onDelete={(id) => void handleDelete(id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
